import { SimpleTooltip } from '../../../../../../shared/frontend/components/presentational/tooltip';
import { naturalShortDate } from '../../../../../../shared/frontend/libs/utils';
import { getHumanReadableSize } from '../../../../../../shared/frontend/scripts/fileExplorer/utils';
import { TCombatLogSegmentSummary } from '../../../../shared/types/combatLogDownloadTypes';
import { TCombatLogSegmentTableRow } from './useCombatLogSegments';

function formatBytesExact(size: number) {
  return `${size.toLocaleString()} bytes`;
}

function formatSegmentId(segment: TCombatLogSegmentSummary) {
  const parts = [String(segment.segmentId)];

  if (segment.isError) {
    parts.push('(error)');
  } else if (segment.type) {
    parts.push(`(${segment.type})`);
  }

  return parts.join(' ');
}

function DownloadButton(props: {
  segmentKey: string;
  isDownloading: boolean;
  onDownload: () => void;
}) {
  return (
    <button
      type="button"
      className="button-link"
      aria-label={`Download ${props.segmentKey}`}
      disabled={props.isDownloading}
      onClick={props.onDownload}
      style={{
        background: 'none',
        border: 0,
        color: '#fff',
        cursor: props.isDownloading ? 'default' : 'pointer',
        fontSize: '1rem',
        lineHeight: 1,
        padding: 0
      }}
    >
      <i className={`fa ${props.isDownloading ? 'fa-spinner fa-spin' : 'fa-download'}`} />
    </button>
  );
}

export default function CombatLogSegmentsTable(props: {
  rows: TCombatLogSegmentTableRow[];
  downloadingKeys: Record<string, boolean>;
  onDownload: (segmentKey: string, filename?: string) => void;
}) {
  return (
    <div className="scrollable-table-container">
      <table className="slds-table rio-rankings-table slds-table--bordered slds-table--striped slds-max-small-table--stacked">
        <thead>
          <tr>
            <th scope="col">
              <div className="slds-truncate slds-text-heading--small slds-text-align--center" style={{ color: '#fff' }}>
                &nbsp;
              </div>
            </th>
            <th scope="col">
              <div className="slds-truncate slds-text-heading--small" style={{ color: '#fff' }}>
                User
              </div>
            </th>
            <th scope="col">
              <div className="slds-truncate slds-text-heading--small slds-text-align--center" style={{ color: '#fff' }}>
                Segment ID
              </div>
            </th>
            <th scope="col">
              <div className="slds-truncate slds-text-heading--small" style={{ color: '#fff', textAlign: 'center' }}>
                Size
              </div>
            </th>
            <th scope="col">
              <div className="slds-truncate slds-text-heading--small" style={{ color: '#fff' }}>
                Date
              </div>
            </th>
            <th scope="col">
              <div className="slds-truncate slds-text-heading--small slds-text-align--right" style={{ color: '#fff' }}>
                &nbsp;
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {props.rows.map((row) => (
            <tr key={row.segment.segmentKey}>
              <td data-label="Status">
                <div
                  className="slds-truncate slds-text-align--center"
                  style={{ color: row.segment.isError ? '#d9534f' : '#2e844a', fontSize: '1rem' }}
                >
                  <i className={`fa ${row.segment.isError ? 'fa-times' : 'fa-check'}`} />
                </div>
              </td>
              <td data-label="User">
                <div className="slds-truncate">
                  <a
                    href={`https://raider.io/admin/user?user=${row.userId}&action=View`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {row.username || `User ${row.userId}`}
                  </a>
                </div>
              </td>
              <td data-label="Segment ID">
                <div className="slds-truncate slds-text-align--center">{formatSegmentId(row.segment)}</div>
              </td>
              <td data-label="Size" className="slds-text-align--center">
                <SimpleTooltip tooltip={formatBytesExact(row.segment.size)} tag="span">
                  <div className="slds-truncate slds-text-align--center">{getHumanReadableSize(row.segment.size)}</div>
                </SimpleTooltip>
              </td>
              <td data-label="Date">
                <div className="slds-truncate">
                  {row.segment.lastModified ? naturalShortDate(row.segment.lastModified) : 'N/A'}
                </div>
              </td>
              <td data-label="Download" className="slds-text-align--right">
                <DownloadButton
                  segmentKey={row.segment.segmentKey}
                  isDownloading={Boolean(props.downloadingKeys[row.segment.segmentKey])}
                  onDownload={() => props.onDownload(row.segment.segmentKey, row.segment.suggestedFilename)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
